import {
  BooleanFilter,
  Currencies,
  CurrenciesLowerCase,
  DateFilterType,
  DateType,
  FetchItemFetched,
  FetchItemsCacheKey,
  FetchItemsClearItemsFilters,
  FetchItemsErrorMessage,
  FetchItemsFetched,
  FetchItemsFilterItems,
  FetchItemsGqlQuery,
  FetchItemsIsPlaceholderData,
  FetchItemsLimit,
  FetchItemsPage,
  FetchItemsPaginateItems,
  FetchItemsPrefetchItems,
  FetchItemsSortItems,
  FetchItemsTotalCount,
  FloatFilterType,
  ID,
  IdFilterType,
  IntegerFilterType,
  MoneyType,
  NanoID,
  NanoIdFilterType,
  TextFilterType
} from '../../types';
import { InvoiceStatus, InvoiceStatusFilter } from '../invoices/invoicesTypes';
import {
  Checked,
  CheckedAll,
  CheckedHashItem,
  CheckedPartial,
  OnCheckAll,
  OnSetCheckedIds
} from '../../common/hooks/useTableCheckable';
import {
  ItemCreatedAt,
  ItemID,
  ItemItemGroupID,
  ItemName,
  ItemNanoID,
  ItemPaid,
  ItemPrice,
  ItemUpdatedAt,
  ItemUUID,
  ItemViewPrice
} from '../items/itemsTypes';
import {
  ProjectCreatedAt,
  ProjectName,
  ProjectNanoID
} from '../projects/projectsTypes';
import {
  TaskCreatedAt,
  TaskName,
  TaskNanoID,
  TaskStatuses,
  TaskStatusFilter
} from '../tasks/tasksTypes';
import {
  SmartContractStatus,
  SmartContractStatusFilterType,
  SmartContractWithoutDoneFilter
} from '../smartContracts/smartContractsTypes';
import { FetchGroupedItemsQueryResponse } from './queries/fetchGroupedItems.query';
import { CacheTaskMoveItemsInput } from '../tasks/hooks/useCacheTaskMoveItems/useCacheTaskMoveItems';
import { UpdateIndexQueryItemsCacheAction } from '../common/reactQuery/hooks/useIndexQuery';
import { ItemCategoryName } from '../itemCategories/itemCategoriesTypes';
import { ItemTypeName } from '../itemTypes/itemTypesTypes';
import { UserID } from '../users/usersTypes';
import { ItemsGroupBindStatus } from '../tasks/components/tables/TasksItemsTable/components/TasksItemsTableItemRow/TasksItemsTableItemRow.types';
import { CurrencyRateID } from '../currencyRates/currencyRatesTypes';

export type GroupedItemUUID = ItemUUID;
export type GroupedItemDescription = string;
export type GroupedItemGroupedBindStatus = ItemsGroupBindStatus;
export type GroupedItemGroupedID = string;
export type GroupedItemPaidAmount = MoneyType;
export type GroupedItemItemID = ItemID;
export type GroupedItemItemUUID = ItemUUID;
export type GroupedItemItemPrice = ItemPrice;
export type GroupedItemItemViewPrice = ItemViewPrice;
export type GroupedItemItemNanoID = ItemNanoID;
export type GroupedItemItemPaid = ItemPaid;
export type GroupedItemItemName = ItemName;
export type GroupedItemItemCategoryName = ItemCategoryName;
export type GroupedItemItemCreatedAt = ItemCreatedAt;
export type GroupedItemItemUpdatedAt = ItemUpdatedAt;
export type GroupedItemItemShowMoveItemButton = boolean;
export type GroupedItemBindingStatus = GroupedItemBindingStatuses;
export type GroupedItemCreationMonth = DateType;
export type GroupedItemMonthTotal = MoneyType;
export type GroupedItemProjectName = ProjectName;
export type GroupedItemProjectNanoID = ProjectNanoID;
export type GroupedItemProjectTotal = MoneyType;
export type GroupedItemQuantity = number;
export type GroupedItemInvoiceStatus = InvoiceStatus;
export type GroupedItemSmartContractStatus = SmartContractStatus;
export type GroupedItemStatusChangedAt = DateType;
export type GroupedItemTaskName = TaskName;
export type GroupedItemTaskNanoID = TaskNanoID;
export type GroupedItemTaskTotal = MoneyType;
export type GroupedItemTaskStatus = TaskStatuses;
export type GroupedItemTotalPrice = MoneyType;
export type GroupedItemProjectCreatedAt = ProjectCreatedAt;
export type GroupedItemProjectClosureMonth = ProjectCreatedAt;
export type GroupedItemRefunded = boolean;
export type GroupedItemTaskCreatedAt = TaskCreatedAt;
export type GroupedItemTaskDueDateAt = DateType;
export type GroupedItemItemsIds = ItemID[];
export type GroupedItemBurning = boolean;
export type GroupedItemShowMoveItemButton = boolean;
export type GroupedItemShowVoidButton = boolean;
export type GroupedItemConvertedPrice = MoneyType;
export type GroupedItemConvertedPricePerItem = MoneyType;
export type GroupedItemConvertedporjectTotal = MoneyType;
export type GroupedItemConvertedTaskTotal = MoneyType;
export type GroupedItemConvertedMonthTotal = MoneyType;
export type GroupedItemViewPrice = MoneyType;
export type GroupedItemViewPriceCurrency = Currencies;
export type GroupedItemItemViewPriceCurrency = Currencies;
export type GroupedItemItemConvertedPrice = MoneyType;
export type GroupedItemItemTypeName = ItemTypeName;
export type GroupedItemItemGroupID = ItemItemGroupID;
export type GroupedItemItemItemCategoryName = ItemCategoryName;
export type GroupedItemItemShowVoidButton = boolean;
export type GroupedItemItemUnbindedAt = DateType;
export type GroupedItemItemSubprojectGroupName = string;
export type GroupedItemItemSubprojectGroupNanoID = NanoID;
export type GroupedItemItemTagName = string;
export type GroupedItemItemTagID = ID;
export type GroupedItemItemUserID = UserID;

export type ItemsTotalsPerStatusAll = number;
export type ItemsTotalsPerStatusOpen = number;
export type ItemsTotalsPerStatusSent = number;
export type ItemsTotalsPerStatusUncollectible = number;

export type GroupedItemBatchConvertedTotal = MoneyType;
export type GroupedItemBatchLabel = string;
export type GroupedItemSplitConvertedPrice = MoneyType;
export type GroupedItemSplitLabel = string;
export type GroupedItemSplitPartNumber = number;
export type GroupedItemSplitPartPercent = number;

export type GroupedItemMetaTotal = MoneyType;
export type GroupedItemMetaTotalOpenRegularCents = MoneyType;
export type GroupedItemMetaTotalPaidAmount = MoneyType;
export type GroupedItemMetaTotalPaidRegularCents = MoneyType;
export type GroupedItemMetaTotalPrice = MoneyType;
export type GroupedItemMetaTotalPriceCents = MoneyType;
export type GroupedItemMetaTotalSentRegularCents = MoneyType;
export type GroupedItemMetaConvertedTotalPrice = MoneyType;
export type GroupedItemMetaConvertedTotalPriceCents = MoneyType;

export type ItemsTotalsPerStatusType = {
  all: ItemsTotalsPerStatusAll;
  open: ItemsTotalsPerStatusOpen;
  sent: ItemsTotalsPerStatusSent;
  uncollectible: ItemsTotalsPerStatusUncollectible;
};

export const enum GroupedItemBindingStatuses {
  BINDED = 'binded',
  MIXED = 'mixed',
  UNBINDED = 'unbinded'
}

export const enum FetchCacheGroupedItemsSortEnum {
  CREATED_AT_ASC = 'CREATED_AT_ASC',
  CREATED_AT_DESC = 'CREATED_AT_DESC',
  ID_DESC = 'ID_DESC',
  ID_ASC = 'ID_ASC',
  CATEGORY_NAME_ASC = 'CATEGORY_NAME_ASC',
  CATEGORY_NAME_DESC = 'CATEGORY_NAME_DESC',
  ITEM_CREATED_AT_ASC = 'ITEM_CREATED_AT_ASC',
  ITEM_CREATED_AT_DESC = 'ITEM_CREATED_AT_DESC',
  PROJECT_CLOSURE_MONTH_ASC = 'PROJECT_CLOSURE_MONTH_ASC',
  PROJECT_CLOSURE_MONTH_DESC = 'PROJECT_CLOSURE_MONTH_DESC',
  PROJECT_CREATED_AT_ASC = 'PROJECT_CREATED_AT_ASC',
  PROJECT_CREATED_AT_DESC = 'PROJECT_CREATED_AT_DESC',
  TASK_CREATED_AT_ASC = 'TASK_CREATED_AT_ASC',
  TASK_CREATED_AT_DESC = 'TASK_CREATED_AT_DESC',
  TASK_DUE_DATE_MONTH_ASC = 'TASK_DUE_DATE_MONTH_ASC',
  TASK_DUE_DATE_MONTH_DESC = 'TASK_DUE_DATE_MONTH_DESC'
}

export const enum FetchGroupedItemsSortEnum {
  BINDING_STATUS_ASC = 'BINDING_STATUS_ASC',
  BINDING_STATUS_DESC = 'BINDING_STATUS_DESC',
  CATEGORY_NAME_ASC = 'CATEGORY_NAME_ASC',
  CATEGORY_NAME_DESC = 'CATEGORY_NAME_DESC',
  CONVERTED_PRICE_ASC = 'CONVERTED_PRICE_ASC',
  CONVERTED_PRICE_DESC = 'CONVERTED_PRICE_DESC',
  CONVERTED_PRICE_PER_ITEM_ASC = 'CONVERTED_PRICE_PER_ITEM_ASC',
  CONVERTED_PRICE_PER_ITEM_DESC = 'CONVERTED_PRICE_PER_ITEM_DESC',
  CREATION_MONTH_ASC = 'CREATION_MONTH_ASC',
  CREATION_MONTH_DESC = 'CREATION_MONTH_DESC',
  INVOICE_STATUS_ASC = 'INVOICE_STATUS_ASC',
  INVOICE_STATUS_DESC = 'INVOICE_STATUS_DESC',
  ITEM_CREATED_AT_ASC = 'ITEM_CREATED_AT_ASC',
  ITEM_CREATED_AT_DESC = 'ITEM_CREATED_AT_DESC',
  ITEM_ID_ASC = 'ITEM_ID_ASC',
  ITEM_ID_DESC = 'ITEM_ID_DESC',
  ITEM_NAME_ASC = 'ITEM_NAME_ASC',
  ITEM_NAME_DESC = 'ITEM_NAME_DESC',
  ITEM_PRICE_ASC = 'ITEM_PRICE_ASC',
  ITEM_PRICE_DESC = 'ITEM_PRICE_DESC',
  PROJECT_CLOSURE_MONTH_ASC = 'PROJECT_CLOSURE_MONTH_ASC',
  PROJECT_CLOSURE_MONTH_DESC = 'PROJECT_CLOSURE_MONTH_DESC',
  PROJECT_CREATED_AT_ASC = 'PROJECT_CREATED_AT_ASC',
  PROJECT_CREATED_AT_DESC = 'PROJECT_CREATED_AT_DESC',
  ROOT_PROJECT_CLOSURE_MONTH_ASC = 'ROOT_PROJECT_CLOSURE_MONTH_ASC',
  ROOT_PROJECT_CLOSURE_MONTH_DESC = 'ROOT_PROJECT_CLOSURE_MONTH_DESC',
  ROOT_PROJECT_CREATED_AT_ASC = 'ROOT_PROJECT_CREATED_AT_ASC',
  ROOT_PROJECT_CREATED_AT_DESC = 'ROOT_PROJECT_CREATED_AT_DESC',
  PROJECT_NAME_ASC = 'PROJECT_NAME_ASC',
  PROJECT_NAME_DESC = 'PROJECT_NAME_DESC',
  QUANTITY_ASC = 'QUANTITY_ASC',
  QUANTITY_DESC = 'QUANTITY_DESC',
  REFUNDED_ASC = 'REFUNDED_ASC',
  REFUNDED_DESC = 'REFUNDED_DESC',
  SMART_CONTRACT_STATUS_ASC = 'SMART_CONTRACT_STATUS_ASC',
  SMART_CONTRACT_STATUS_DESC = 'SMART_CONTRACT_STATUS_DESC',
  STATUS_CHANGED_AT_ASC = 'STATUS_CHANGED_AT_ASC',
  STATUS_CHANGED_AT_DESC = 'STATUS_CHANGED_AT_DESC',
  TAG_NAME_ASC = 'TAG_NAME_ASC',
  TAG_NAME_DESC = 'TAG_NAME_DESC',
  TASK_CREATED_AT_ASC = 'TASK_CREATED_AT_ASC',
  TASK_CREATED_AT_DESC = 'TASK_CREATED_AT_DESC',
  TASK_DUE_DATE_MONTH_ASC = 'TASK_DUE_DATE_MONTH_ASC',
  TASK_DUE_DATE_MONTH_DESC = 'TASK_DUE_DATE_MONTH_DESC',
  TASK_NAME_ASC = 'TASK_NAME_ASC',
  TASK_NAME_DESC = 'TASK_NAME_DESC',
  TASK_TOTAL_ASC = 'TASK_TOTAL_ASC',
  TASK_TOTAL_DESC = 'TASK_TOTAL_DESC',
  TEMP_INVOICE_NAME_ASC = 'TEMP_INVOICE_NAME_ASC',
  TEMP_INVOICE_NAME_DESC = 'TEMP_INVOICE_NAME_DESC',
  TOTAL_PRICE_ASC = 'TOTAL_PRICE_ASC',
  TOTAL_PRICE_DESC = 'TOTAL_PRICE_DESC',
  INVOICE_TAG_ASC = 'INVOICE_TAG_ASC',
  INVOICE_TAG_DESC = 'INVOICE_TAG_DESC',
  INVOICE_CREATION_MONTH_ASC = 'INVOICE_CREATION_MONTH_ASC',
  INVOICE_CREATION_MONTH_DESC = 'INVOICE_CREATION_MONTH_DESC'
}

export const enum FetchGroupedItemsSortableFields {
  BINDING_STATUS = 'bindingStatus',
  CATEGORY_NAME = 'categoryName',
  CONVERTED_PRICE = 'convertedPrice',
  CONVERTED_PRICE_PER_ITEM = 'convertedPricePerItem',
  CREATION_MONTH = 'creationMonth',
  INVOICE_STATUS = 'invoiceStatus',
  ITEM_CREATED_AT = 'itemCreatedAt',
  ITEM_ID = 'itemId',
  ITEM_NAME = 'itemName',
  ITEM_PRICE = 'itemPrice',
  PROJECT_CREATED_AT = 'projectCreatedAt',
  PROJECT_NAME = 'projectName',
  QUANTITY = 'quantity',
  REFUNDED = 'refunded',
  SMART_CONTRACT_STATUS = 'smartContractsStatus',
  STATUS_CHANGED_AT = 'statusChangedAt',
  TAG_NAME = 'tagName',
  TASK_CREATED_AT = 'taskCreatedAt',
  TASK_NAME = 'taskName',
  TASK_TOTAL = 'taskTotal',
  TEMP_INVOICE_NAME = 'tempInvoiceName',
  TOTAL_PRICE = 'totalPrice'
}

export interface FetchCacheGroupedItemsFilters {
  taskNanoId?: NanoIdFilterType;
  id?: IdFilterType;
  companyNanoId?: NanoIdFilterType;
  descendantsProjectNanoId?: NanoIdFilterType;
  generalLedgerId?: IdFilterType;
  invoiceStatus?: InvoiceStatusFilter;
  parentInvoiceNanoId?: NanoIdFilterType;
  status?: SmartContractStatusFilterType;
  tempInvoiceId?: IdFilterType;
  withoutDone?: SmartContractWithoutDoneFilter;
  withoutParentInvoice?: BooleanFilter;
  parentOrTempInvoiceNanoId?: NanoIdFilterType;
  projectNanoId?: NanoIdFilterType;
}

export const enum FetchCacheGroupedItemsFilterFields {
  DESCENDANTS_PROJECT_NANO_ID = 'descendantsProjectNanoId',
  PARENT_INVOICE_NANO_ID = 'parentInvoiceNanoId',
  STATUS = 'status',
  WITHOUT_DONE = 'withoutDone'
}

export interface FetchGroupedItemsFilters {
  bindingStatus?: TextFilterType;
  companyNanoId?: NanoIdFilterType;
  convertedPrice?: FloatFilterType;
  convertedPricePerItem?: FloatFilterType;
  currentOnly?: BooleanFilter;
  generalLedgerId?: IdFilterType;
  id?: IdFilterType;
  invoiceNanoId?: NanoIdFilterType;
  invoiceStatus?: InvoiceStatusFilter;
  invoiceStatusChangedAt?: DateFilterType;
  itemCreatedAt?: DateFilterType;
  itemName?: TextFilterType;
  itemTagId?: IdFilterType;
  parentInvoiceNanoId?: NanoIdFilterType;
  parentOrTempInvoiceNanoId?: NanoIdFilterType;
  priceCents?: IntegerFilterType;
  projectCreatedAt?: DateFilterType;
  projectName?: TextFilterType;
  projectNanoId?: NanoIdFilterType;
  quantity?: IntegerFilterType;
  smartContractStatus?: SmartContractStatusFilterType;
  taskCreatedAt?: DateFilterType;
  taskName?: TextFilterType;
  taskNanoId?: NanoIdFilterType;
  taskStatus?: TaskStatusFilter;
  tempInvoiceId?: IdFilterType;
  tempInvoiceNanoId?: NanoIdFilterType;
  totalPrice?: IntegerFilterType;
  withoutParentInvoice?: BooleanFilter;
}

export const enum FetchGroupedItemsFilterFields {
  BINDING_STATUS = 'bindingStatus',
  COMPANY_NANO_ID = 'companyNanoId',
  CONVERTED_PRICE = 'convertedPrice',
  CONVERTED_PRICE_PER_ITEM = 'convertedPricePerItem',
  ID = 'id',
  INVOICE_STATUS = 'invoiceStatus',
  INVOICE_STATUS_CHANGED_AT = 'invoiceStatusChangedAt',
  ITEM_CREATED_AT = 'itemCreatedAt',
  ITEM_NAME = 'itemName',
  ITEM_TAG_ID = 'itemTagId',
  PRICE_CENTS = 'priceCents',
  PROJECT_CREATED_AT = 'projectCreatedAt',
  PROJECT_NAME = 'projectName',
  PROJECT_NANO_ID = 'projectNanoId',
  QUANTITY = 'quantity',
  SMART_CONTRACT_STATUS = 'smartContractStatus',
  TASK_CREATED_AT = 'taskCreatedAt',
  TASK_NAME = 'taskName',
  TASK_STATUS = 'taskStatus',
  TEMP_INVOICE_ID = 'tempInvoiceId',
  TOTAL_PRICE = 'totalPrice',
  WITHOUT_PARENT_INVOICE = 'withoutParentInvoice'
}

export const enum GroupedItemsGroupByEnum {
  BATCH = 'batch',
  CATEGORY = 'category',
  DATE = 'date',
  FIRST_SUBPROJECT = 'first_subproject',
  INVOICE_CREATION_MONTH = 'invoice_creation_month',
  INVOICE_TAG = 'invoice_tag',
  ITEM = 'item',
  ITEM_GROUP = 'item_group',
  ITEM_TAGS = 'item_tags',
  PROJECT = 'project',
  PROJECT_CLOSURE_MONTH = 'project_closure_month',
  SPLIT = 'split',
  TASK = 'task',
  TASK_DUE_MONTH = 'task_due_month',
  ROOT_PROJECT = 'root_project'
}

export const enum CacheGroupedItemsGroupByEnum {
  BATCH = 'BATCH',
  CATEGORY = 'CATEGORY',
  DATE = 'DATE',
  FIRST_SUBPROJECT = 'FIRST_SUBPROJECT',
  INVOICE_CREATION_MONTH = 'INVOICE_CREATION_MONTH',
  INVOICE_TAG = 'INVOICE_TAG',
  ITEM = 'ITEM',
  ITEM_GROUP = 'ITEM_GROUP',
  ITEM_TAGS = 'ITEM_TAGS',
  PROJECT = 'PROJECT',
  PROJECT_CLOSURE_MONTH = 'PROJECT_CLOSURE_MONTH',
  SMART_CONTRACT_STATUS = 'SMART_CONTRACT_STATUS',
  SPLIT = 'SPLIT',
  TASK = 'TASK',
  TASK_DUE_MONTH = 'TASK_DUE_MONTH',
  ROOT_PROJECT = 'ROOT_PROJECT'
}

export type GroupedItemsCurrencyRateID = CurrencyRateID;
export type CacheGroupedItemsCurrencies = Currencies;
export type GroupedItemsCurrencies = CurrenciesLowerCase;
export type FetchCacheGroupedItemsSortType = FetchCacheGroupedItemsSortEnum[]; //FetchItemsSort;
export type FetchGroupedItemsSortType = FetchGroupedItemsSortEnum[]; //FetchItemsSort;
export type FetchGroupedItemsSort = FetchCacheGroupedItemsSortEnum[];
export type FetchGroupedItemsSortItems = FetchItemsSortItems;
export type FetchGroupedItemsPage = FetchItemsPage;
export type FetchGroupedItemsLimit = FetchItemsLimit;
export type FetchGroupedItemsErrorMessage = FetchItemsErrorMessage;
export type FetchGroupedItemsFetched = FetchItemsFetched;
export type FetchGroupedItemFetched = FetchItemFetched;
export type FetchGroupedItemsIsPlaceholderData = FetchItemsIsPlaceholderData;

export type FetchGroupedItemsClearGroupedItemsFilters =
  FetchItemsClearItemsFilters;
export type FetchGroupedItemsPaginateItems = FetchItemsPaginateItems;
export type FetchGroupedItemsPrefetchItems = FetchItemsPrefetchItems;
export type FetchGroupedItemsTotalCount = FetchItemsTotalCount;
export type FetchGroupedItemsFilterItems =
  FetchItemsFilterItems<FetchGroupedItemsFilters>;

export type ChangeGroupedItemsFiltersFunc = (
  changedFilters: FetchGroupedItemsFilters,
  removeFilters?: string[]
) => void;

export type ChangeCacheGroupedItemsFiltersFunc = (
  changedFilters: FetchCacheGroupedItemsFilters,
  removeFilters?: string[]
) => void;

export type FetchGroupedItemsGqlQuery = FetchItemsGqlQuery;
export type FetchGroupedItemsCacheKey = FetchItemsCacheKey;
export type FetchGroupedItemsCacheKeys = FetchGroupedItemsCacheKey[];

export type FetchGroupedItemsChecked = Checked;
export type FetchGroupedItemsCheckedHash = CheckedHashItem;
export type FetchGroupedItemsOnSetCheckedIds = OnSetCheckedIds;
export type FetchGroupedItemsCheckedAll = CheckedAll;
export type FetchGroupedItemsCheckedPartial = CheckedPartial;
export type FetchGroupedItemsOnCheckAll = OnCheckAll;

export type UpdateGroupedItemsCacheAction = UpdateIndexQueryItemsCacheAction<
  FetchGroupedItemsQueryResponse,
  CacheTaskMoveItemsInput
>;

export interface DnDGroupedItem {
  id: GroupedItemUUID | ItemID;
  dragIds: string[];
  name: ItemTypeName;
  uuid: GroupedItemUUID;
}
